(() => {
  if (customElements.get('eventforge-calendar')) return;

  const translations = {
    All: 'Alle',
    Pokemon: 'Pokémon',
    Casual: 'Locker',
    Competitive: 'Kompetitiv',
    'New Player Friendly': 'Einsteigerfreundlich',
    Free: 'Kostenlos',
    'Sold out': 'Ausverkauft',
    'Selling fast': 'Wird knapp',
    'Almost full': 'Fast ausgebucht'
  };

  const eventforgeApiBase = 'https://clvaspfvdsjwotmfhrgy.supabase.co';
  const requestCache = new Map();
  const eventforgePublicKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNsdmFzcGZ2ZHNqd290bWZocmd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEzMzc0NDksImV4cCI6MjA4NjkxMzQ0OX0.TQuiqUV7_SA676CXHXIGNamFrM2-KFliJdZav2wPEVc';

  class EventforgeCalendar extends HTMLElement {
    constructor() {
      super();
      this.events = [];
      this.query = '';
      this.filters = { location: '', game: '' };
      this.eventDisplay = 'compact';
      this.dayEventSelection = new Map();
      this.pastWeeksExpanded = new Set();
      this.waitlistEvent = null;
      this.waitlistDialog = null;
      this.freeRegistrationEvent = null;
      this.freeRegistrationDialog = null;
      this.freeRegistrationGeneration = 0;
      this.freeRegistrationPending = null;
      const now = new Date();
      this.currentMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
    }

    connectedCallback() {
      try { this.mobileLabels = JSON.parse(this.querySelector('[data-calendar-mobile-labels]')?.textContent || '{}'); }
      catch (_error) { this.mobileLabels = {}; }
      this.feedUrl = this.dataset.feedUrl;
      this.storeUrl = this.safeUrl(this.dataset.storeUrl) || 'https://eventforge.online';
      this.timeZone = this.dataset.timeZone || 'Europe/Berlin';
      this.layout = this.dataset.layout || 'classic';
      this.currency = window.Shopify?.currency?.active || 'EUR';
      this.customer = {
        firstName: this.dataset.customerFirstName || '',
        lastName: this.dataset.customerLastName || '',
        email: this.dataset.customerEmail || ''
      };
      this.status = this.querySelector('[data-calendar-status]');
      this.content = this.querySelector('[data-calendar-content]');
      this.monthLabel = this.querySelector('[data-calendar-month]');
      this.grid = this.querySelector('[data-calendar-grid]');
      this.cards = this.querySelector('[data-calendar-cards]');
      this.listHeading = this.querySelector('[data-calendar-list-heading]');
      this.filterContainer = this.querySelector('[data-calendar-filters]');
      this.search = this.querySelector('[data-calendar-search]');
      this.calendarRegion = this.grid?.parentElement;
      this.selectedDay = this.dateKey(new Date());
      const today = this.dateParts(new Date());
      this.currentMonth = new Date(Date.UTC(Number(today.year), Number(today.month) - 1, 1));
      this.setupDayView();
      this.mobileMedia = window.matchMedia?.('(max-width: 749px)');
      this.mobile = Boolean(this.mobileMedia?.matches);
      this.handleViewportChange = (event) => {
        const previousUrl = this.monthFeedUrl();
        this.mobile = event.matches;
        if (!this.mobile) this.closeEventDialog();
        if (previousUrl !== this.monthFeedUrl()) this.loadEvents();
        else this.render();
      };
      this.mobileMedia?.addEventListener('change', this.handleViewportChange);

      this.querySelector('[data-calendar-previous]')?.addEventListener('click', () => this.changePeriod(-1));
      this.querySelector('[data-calendar-next]')?.addEventListener('click', () => this.changePeriod(1));
      this.querySelector('[data-calendar-today]')?.addEventListener('click', () => this.showToday());
      this.querySelector('[data-calendar-past-weeks]')?.addEventListener('click', () => this.togglePastWeeks());
      this.querySelectorAll('[data-calendar-view]').forEach((button) => {
        button.addEventListener('click', () => this.selectView(button.dataset.calendarView));
      });
      this.querySelectorAll('[data-calendar-display]').forEach((button) => {
        button.addEventListener('click', () => this.selectEventDisplay(button.dataset.calendarDisplay));
      });
      this.search?.addEventListener('input', (event) => {
        this.query = event.target.value.trim().toLocaleLowerCase('de-DE');
        this.render();
      });
      this.filterContainer?.addEventListener('click', (event) => this.handleFilterClick(event));

      this.loadEvents();
      this.refreshTimer = setInterval(() => {
        const eventState = this.events.map((event) => this.calendarEventState(event)).join(',');
        if (eventState !== this.renderedEventState) this.render();
      }, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.refreshTimer);
      this.closeEventDialog();
      this.eventDialog?.remove();
      this.eventDialog = null;
      this.mobileMedia?.removeEventListener('change', this.handleViewportChange);
      this.loadRequestId = (this.loadRequestId || 0) + 1;
      this.closeFreeRegistration();
      this.freeRegistrationDialog?.remove();
      this.freeRegistrationDialog = null;
      if (!this.waitlistDialog) return;
      if (this.waitlistDialog.open && typeof this.waitlistDialog.close === 'function') {
        this.waitlistDialog.close();
      }
      this.waitlistDialog.remove();
      this.waitlistDialog = null;
      this.waitlistEvent = null;
      document.documentElement.classList.remove('eventforge-waitlist-open');
    }

    async loadEvents() {
      if (!this.feedUrl) {
        this.showError('Bitte hinterlege im Theme-Editor die EventForge-Feed-Adresse.');
        return;
      }

      const requestId = this.loadRequestId = (this.loadRequestId || 0) + 1;
      const feedUrl = this.monthFeedUrl();
      this.showLoading();
      const cachedFeed = this.readFeedCache(feedUrl);
      if (cachedFeed) this.applyFeed(cachedFeed);

      try {
        const feed = await this.fetchJsonOnce(feedUrl, {
          headers: { Accept: 'application/feed+json, application/json' }
        }, 'Feed request');
        if (requestId !== this.loadRequestId) return;
        this.applyFeed(feed);
        this.writeFeedCache(feed, feedUrl);
        this.enrichAndRefreshEvents(requestId);
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        requestCache.delete(`GET:${feedUrl}:`);
        console.error('EventForge calendar:', error);
        if (!cachedFeed) {
          this.showError('Der Eventkalender konnte gerade nicht geladen werden. Bitte versuche es später erneut.');
        } else {
          this.enrichAndRefreshEvents(requestId);
        }
      }
    }

    monthFeedUrl() {
      const url = new URL(this.feedUrl, window.location.origin);
      const year = this.currentMonth.getUTCFullYear();
      const month = this.currentMonth.getUTCMonth();
      const monthKey = this.currentMonthKey();
      url.searchParams.set('format', 'json');
      url.searchParams.set('from', `${monthKey}-01`);
      url.searchParams.set('to', `${monthKey}-${new Date(Date.UTC(year, month + 1, 0)).getUTCDate()}`);
      if (this.activeView() === 'week') {
        const { from, to } = this.weekRange();
        if (from < url.searchParams.get('from')) url.searchParams.set('from', from);
        if (to > url.searchParams.get('to')) url.searchParams.set('to', to);
      }
      url.searchParams.set('limit', '250');
      url.searchParams.delete('days');
      return url.href;
    }

    applyFeed(feed) {
      if (!Array.isArray(feed?.items)) throw new Error('Invalid EventForge calendar feed');
      this.events = feed.items
        .map((item) => this.normalizeEvent(item))
        .filter(Boolean)
        .sort((a, b) => a.date - b.date);
      this.renderFilters();
      this.setLoading(false);
      this.render();
    }

    feedCacheKey(feedUrl) {
      return `eventforge-calendar-feed:${feedUrl}`;
    }

    readFeedCache(feedUrl) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.feedCacheKey(feedUrl)) || 'null');
        if (!Array.isArray(cached?.feed?.items) || Date.now() - cached.savedAt > 5 * 60 * 1000) return null;
        return cached.feed;
      } catch (_error) {
        return null;
      }
    }

    writeFeedCache(feed, feedUrl) {
      try {
        sessionStorage.setItem(this.feedCacheKey(feedUrl), JSON.stringify({ savedAt: Date.now(), feed }));
      } catch (_error) {
        // Storage can be unavailable in privacy modes; the network cache still applies.
      }
    }

    async enrichAndRefreshEvents(requestId) {
      try {
        await this.enrichEvents();
        if (requestId !== this.loadRequestId) return;
        this.renderFilters();
        this.render();
      } catch (error) {
        console.warn('EventForge calendar enrichment:', error);
      }

      if (requestId !== this.loadRequestId) return;
      try {
        await this.resolveShopifyProductLinks();
        if (requestId !== this.loadRequestId) return;
        this.render();
      } catch (error) {
        console.warn('EventForge Shopify product resolution:', error);
      }
    }

    async fetchJsonOnce(url, options = {}, errorLabel = 'Request') {
      const method = options.method || 'GET';
      const cacheKey = `${method}:${url}:${options.body || ''}`;
      if (!requestCache.has(cacheKey)) {
        const request = fetch(url, options)
          .then(async (response) => {
            if (!response.ok) throw new Error(`${errorLabel} failed: ${response.status}`);
            return response.json();
          })
          .catch((error) => {
            requestCache.delete(cacheKey);
            throw error;
          });
        requestCache.set(cacheKey, request);
      }
      return requestCache.get(cacheKey);
    }

    normalizeEvent(item) {
      const date = new Date(item.date_published || item.date_modified || '');
      if (Number.isNaN(date.getTime())) return null;

      const details = this.extractDetails(item.content_html || item.content_text || '');
      const extension = item._eventforge || item.eventforge || {};
      const location = this.firstText(item.location, extension.location, extension.venue, item._location);
      const game = this.firstText(item.category, extension.category, item._category);
      const format = this.firstText(item.format, extension.format, item._format);

      return {
        id: String(item.id || item.url || `${date.toISOString()}-${item.title || ''}`),
        eventforgeId: this.eventforgeId(item),
        organizationId: this.firstText(item.organization_id, extension.organization_id),
        url: this.safeUrl(item.url || item.id) || this.storeUrl,
        shopifyProductId: this.firstText(item.shopify_product_id, extension.shopify_product_id),
        shopifyUrl: '',
        title: String(item.title || 'Event'),
        image: this.safeUrl(item.image || item.banner_image),
        date,
        endDate: null,
        dateKey: this.dateKey(date),
        description: details.description,
        price: this.translate(details.price),
        isFree: this.firstBoolean(item.is_free, extension.is_free),
        paymentMode: this.firstText(item.payment_mode, extension.payment_mode),
        registrationRequired: this.firstBoolean(item.registration_required, extension.registration_required),
        freeRegistrationSupported: false,
        registrationCutoffMinutes: null,
        hasPaidPricing: [item.price_cents, extension.price_cents, item.pay_in_store_price_cents, extension.pay_in_store_price_cents]
          .some((price) => this.isNumericValue(price) && Number(price) > 0),
        capacity: null,
        confirmedCount: null,
        remainingSlots: null,
        location,
        games: game ? [game] : [],
        formats: format ? [format] : [],
        featured: Boolean(item.featured ?? extension.featured),
        soldOut: Boolean(item.sold_out ?? item.soldOut ?? extension.sold_out ?? extension.soldOut),
        waitlistEnabled: this.firstBoolean(item.waitlist_enabled, extension.waitlist_enabled)
      };
    }

    eventforgeId(item) {
      const values = [item.id, item.url];
      const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
      for (const value of values) {
        const match = String(value || '').match(uuidPattern);
        if (match) return match[0];
      }
      return '';
    }

    async enrichEvents() {
      const events = this.events;
      const eventIds = [...new Set(events.map((event) => event.eventforgeId).filter(Boolean))];
      if (!eventIds.length) return;

      const eventRows = await this.fetchEventforgeTable('events_public', {
        id: `in.(${eventIds.join(',')})`,
        select: 'id,organization_id,location_id,shopify_product_id,start_time,end_time,capacity,is_free,payment_mode,registration_required,price_cents,pay_in_store_price_cents,waitlist_enabled,status,has_access_code,required_badge_key,badge_early_access_until,collection_id,team_registration_mode,event_type,booking_duration_minutes,slot_interval_minutes,reservation_asset_ids,booking_window_mode,booking_window_days,member_early_rsvp_at,registration_cutoff_minutes'
      });
      const eventRowsById = new Map(eventRows.map((row) => [String(row.id), row]));
      events.forEach((event) => {
        const row = eventRowsById.get(event.eventforgeId);
        const endDate = row?.end_time ? new Date(row.end_time) : null;
        if (endDate && Number.isFinite(endDate.getTime()) && endDate >= event.date) event.endDate = endDate;
      });
      if (events === this.events) this.render();
      const locationIds = [...new Set(eventRows.map((row) => row.location_id).filter(Boolean))];

      const [locations, assignments, pricingTiers, registrationCounts] = await Promise.all([
        locationIds.length
          ? this.fetchEventforgeTable('locations', {
            id: `in.(${locationIds.join(',')})`,
            select: 'id,name'
          })
          : [],
        this.fetchEventforgeTable('event_tag_assignments', {
          event_id: `in.(${eventIds.join(',')})`,
          select: 'event_id,tag_id'
        }),
        this.fetchEventforgeTable('event_pricing_tiers', {
          event_id: `in.(${eventIds.join(',')})`,
          select: 'event_id,name,price_cents,sort_order'
          }).catch(() => null),
        this.fetchRegistrationCounts(eventIds).catch(() => new Map())
      ]);

      const tagIds = [...new Set(assignments.map((assignment) => assignment.tag_id).filter(Boolean))];
      const tags = tagIds.length
        ? await this.fetchEventforgeTable('event_tags', {
          id: `in.(${tagIds.join(',')})`,
          select: 'id,name,tag_type'
        })
        : [];
      const locationsById = new Map(locations.map((location) => [String(location.id), location]));
      const tagsById = new Map(tags.map((tag) => [String(tag.id), tag]));
      const assignmentsByEvent = new Map();
      const pricingTiersByEvent = new Map();

      assignments.forEach((assignment) => {
        const eventId = String(assignment.event_id);
        if (!assignmentsByEvent.has(eventId)) assignmentsByEvent.set(eventId, []);
        const tag = tagsById.get(String(assignment.tag_id));
        if (tag) assignmentsByEvent.get(eventId).push(tag);
      });

      (pricingTiers || []).forEach((tier) => {
        const eventId = String(tier.event_id);
        if (!pricingTiersByEvent.has(eventId)) pricingTiersByEvent.set(eventId, []);
        pricingTiersByEvent.get(eventId).push(tier);
      });

      events.forEach((event) => {
        const row = eventRowsById.get(event.eventforgeId);
        if (!row) return;
        const location = locationsById.get(String(row.location_id || ''));
        const eventTags = assignmentsByEvent.get(event.eventforgeId) || [];
        const games = eventTags.filter((tag) => tag.tag_type === 'primary').map((tag) => tag.name).filter(Boolean);
        const formats = eventTags.filter((tag) => tag.tag_type === 'secondary').map((tag) => tag.name).filter(Boolean);
        const tiers = pricingTiersByEvent.get(event.eventforgeId) || [];
        const tierPrices = tiers
          .filter((tier) => this.isNumericValue(tier.price_cents))
          .map((tier) => Number(tier.price_cents));
        const capacity = Number(row.capacity);
        const confirmedCount = registrationCounts.get(event.eventforgeId);

        if (row.organization_id) event.organizationId = String(row.organization_id);
        event.isFree = this.firstBoolean(row.is_free);
        event.paymentMode = this.firstText(row.payment_mode);
        event.registrationRequired = this.firstBoolean(row.registration_required);
        event.hasPaidPricing = [...tierPrices, row.price_cents, row.pay_in_store_price_cents]
          .some((price) => this.isNumericValue(price) && Number(price) > 0);
        if (typeof row.waitlist_enabled === 'boolean') event.waitlistEnabled = row.waitlist_enabled;
        if (location?.name) event.location = location.name;
        if (games.length) event.games = [...new Set(games)];
        if (formats.length) event.formats = [...new Set(formats)];
        if (row.shopify_product_id) event.shopifyProductId = String(row.shopify_product_id);
        if (tierPrices.length) {
          event.price = this.formatPriceRangeCents(tierPrices);
        } else if (row.is_free === true) {
          event.price = 'Kostenlos';
        } else if (this.isNumericValue(row.price_cents)) {
          event.price = this.formatPriceCents(Number(row.price_cents));
        } else if (this.isNumericValue(row.pay_in_store_price_cents)) {
          event.price = this.formatPriceCents(Number(row.pay_in_store_price_cents));
        }
        if (Number.isFinite(capacity) && capacity > 0) {
          event.capacity = capacity;
          if (Number.isFinite(confirmedCount)) {
            event.confirmedCount = confirmedCount;
            event.remainingSlots = Math.max(0, capacity - confirmedCount);
            event.soldOut = event.remainingSlots === 0;
          }
        }
      });
      await this.enrichFreeRegistrationOptions(events, eventRows, pricingTiers);
    }

    async enrichFreeRegistrationOptions(events, rows, pricingTiers) {
      events.forEach((event) => { event.freeRegistrationSupported = false; });
      const candidates = rows.filter((row) => row.is_free === true && row.payment_mode === 'free' &&
        row.registration_required !== false && !row.shopify_product_id && row.status === 'published');
      if (!candidates.length || !Array.isArray(pricingTiers)) return;
      const ids = candidates.map((row) => String(row.id));
      const organizationIds = [...new Set(candidates.map((row) => row.organization_id).filter(Boolean))];
      if (!organizationIds.length) return;
      try {
        const [fields, timeslots, organizations] = await Promise.all([
          this.fetchEventforgeTable('event_custom_fields', { event_id: `in.(${ids.join(',')})`, select: 'event_id' }),
          this.fetchEventforgeTable('event_timeslots', { event_id: `in.(${ids.join(',')})`, select: 'event_id' }),
          this.fetchEventforgeTable('organizations_public', {
            id: `in.(${organizationIds.join(',')})`,
            select: 'id,registration_cutoff_minutes,max_advance_booking_days,max_advance_booking_days_recurring,max_advance_booking_days_featured,max_advance_booking_days_non_featured,deactivated_at'
          })
        ]);
        candidates.forEach((row) => {
          const event = events.find((item) => item.eventforgeId === row.id);
          const organization = organizations.find((item) => item.id === row.organization_id);
          if (!event || !organization || organization.deactivated_at) return;
          const cutoff = row.registration_cutoff_minutes ?? organization.registration_cutoff_minutes;
          if (!this.isNumericValue(cutoff) || Number(cutoff) < 0) return;
          event.registrationCutoffMinutes = Number(cutoff);
          const hasBookingWindow = Object.keys(organization).some((key) => key.startsWith('max_advance_booking_days') && organization[key] != null);
          const hasExtraFields = [fields, timeslots, pricingTiers].some((items) => items.some((item) => item.event_id === row.id));
          const simpleEvent = row.has_access_code === false && !row.required_badge_key && !row.badge_early_access_until &&
            !row.collection_id && (!row.team_registration_mode || row.team_registration_mode === 'disabled') &&
            row.event_type === 'event' && !row.booking_duration_minutes && !row.slot_interval_minutes &&
            (!row.reservation_asset_ids || Array.isArray(row.reservation_asset_ids) && row.reservation_asset_ids.length === 0) &&
            ['inherit', 'open_immediately'].includes(row.booking_window_mode) && row.booking_window_days == null &&
            !row.member_early_rsvp_at;
          event.freeRegistrationSupported = simpleEvent && !hasBookingWindow && !hasExtraFields;
        });
      } catch (_error) {
        // Unknown registration requirements leave the event informational.
      }
    }

    async fetchEventforgeTable(table, parameters) {
      const url = new URL(`/rest/v1/${table}`, eventforgeApiBase);
      Object.entries(parameters).forEach(([key, value]) => url.searchParams.set(key, value));
      const data = await this.fetchJsonOnce(url.href, {
        headers: {
          Accept: 'application/json',
          apikey: eventforgePublicKey,
          Authorization: `Bearer ${eventforgePublicKey}`
        }
      }, `${table} request`);
      if (!Array.isArray(data)) throw new Error(`Invalid ${table} response`);
      return data;
    }

    async fetchRegistrationCounts(eventIds) {
      const counts = new Map();
      for (let index = 0; index < eventIds.length; index += 200) {
        const url = new URL('/rest/v1/rpc/get_registration_counts', eventforgeApiBase);
        const rows = await this.fetchJsonOnce(url.href, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            apikey: eventforgePublicKey,
            Authorization: `Bearer ${eventforgePublicKey}`
          },
          body: JSON.stringify({ _event_ids: eventIds.slice(index, index + 200) })
        }, 'Registration counts request');
        if (!Array.isArray(rows)) throw new Error('Invalid registration counts response');
        eventIds.slice(index, index + 200).forEach((id) => counts.set(id, 0));
        rows.forEach((row) => {
          counts.set(String(row.event_id), Number(row.seats) || 0);
        });
      }
      return counts;
    }

    async resolveShopifyProductLinks() {
      const linkedEvents = this.events.filter((event) => event.shopifyProductId);
      if (!linkedEvents.length) return;

      const products = await this.fetchShopifyProducts().catch(() => []);
      const productsById = new Map(products.map((product) => [String(product.id), product]));
      const unresolved = [];

      linkedEvents.forEach((event) => {
        const product = productsById.get(event.shopifyProductId);
        if (!this.applyShopifyProduct(event, product)) unresolved.push(event);
      });

      for (let index = 0; index < unresolved.length; index += 5) {
        const batch = unresolved.slice(index, index + 5);
        await Promise.all(batch.map(async (event) => {
          const product = await this.findShopifyProduct(event);
          this.applyShopifyProduct(event, product);
        }));
      }
    }

    async fetchShopifyProducts() {
      const url = new URL(`${this.shopifyRoot()}products.json`, window.location.origin);
      url.searchParams.set('limit', '250');
      const data = await this.fetchJsonOnce(url.href, { headers: { Accept: 'application/json' } }, 'Shopify products request');
      return Array.isArray(data.products) ? data.products : [];
    }

    async findShopifyProduct(event) {
      try {
        const url = new URL(`${this.shopifyRoot()}search/suggest.json`, window.location.origin);
        url.searchParams.set('q', [event.title, event.location, 'Ticket'].filter(Boolean).join(' '));
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        const data = await this.fetchJsonOnce(url.href, { headers: { Accept: 'application/json' } }, 'Shopify product search');
        const products = data?.resources?.results?.products || [];
        return products.find((candidate) => String(candidate.id) === event.shopifyProductId) || null;
      } catch (_error) {
        return null;
      }
    }

    shopifyRoot() {
      const root = window.Shopify?.routes?.root;
      return typeof root === 'string' && root.startsWith('/') ? root : '/';
    }

    shopifyProductUrl(product) {
      if (!product) return '';
      if (product.handle) {
        return this.safeUrl(`${this.shopifyRoot()}products/${encodeURIComponent(product.handle)}`);
      }
      return this.safeUrl(product.url);
    }

    applyShopifyProduct(event, product) {
      const productUrl = this.shopifyProductUrl(product);
      if (!productUrl) return false;
      event.shopifyUrl = productUrl;
      const price = this.shopifyProductPrice(product);
      if (price) event.price = price;
      return true;
    }

    shopifyProductPrice(product) {
      const variantPrices = Array.isArray(product?.variants)
        ? product.variants
          .filter((variant) => this.isNumericValue(variant.price))
          .map((variant) => Math.round(Number(variant.price) * 100))
        : [];
      if (variantPrices.length) return this.formatPriceRangeCents(variantPrices);
      if (this.isNumericValue(product?.price)) {
        return this.formatPriceCents(Math.round(Number(product.price) * 100));
      }
      return '';
    }

    isNumericValue(value) {
      return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
    }

    formatPriceRangeCents(prices) {
      const sortedPrices = [...new Set(prices)].sort((a, b) => a - b);
      if (!sortedPrices.length) return '';
      if (sortedPrices[0] === sortedPrices[sortedPrices.length - 1]) {
        return this.formatPriceCents(sortedPrices[0]);
      }
      return `${this.formatPriceCents(sortedPrices[0])} – ${this.formatPriceCents(sortedPrices[sortedPrices.length - 1])}`;
    }

    formatPriceCents(priceCents) {
      if (Number(priceCents) === 0) return 'Kostenlos';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: this.currency || 'EUR'
      }).format(Number(priceCents) / 100);
    }

    extractDetails(html) {
      if (!html) return { description: '', price: '' };
      const documentFragment = new DOMParser().parseFromString(String(html), 'text/html');
      const lines = Array.from(documentFragment.body.querySelectorAll('p, li'))
        .map((node) => node.textContent.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .filter((line) => !/^(when|wann):/i.test(line));
      const price = lines.find((line) => /^(free|kostenlos)$/i.test(line) || /(?:€|eur|\$|usd)/i.test(line)) || '';
      const description = lines.find((line) => line !== price) || '';
      return { description, price };
    }

    firstText(...values) {
      for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (value && typeof value === 'object') {
          const nested = value.name || value.title || value.label;
          if (typeof nested === 'string' && nested.trim()) return nested.trim();
        }
      }
      return '';
    }

    firstBoolean(...values) {
      for (const value of values) {
        if (typeof value === 'boolean') return value;
      }
      return null;
    }

    safeUrl(value) {
      if (!value) return '';
      try {
        const url = new URL(String(value), window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch (_error) {
        return '';
      }
    }

    translate(value) {
      return translations[value] || value || '';
    }

    dateParts(date) {
      const parts = new Intl.DateTimeFormat('de-DE', {
        timeZone: this.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(date);
      return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
    }

    dateKey(date) {
      const parts = this.dateParts(date);
      return `${parts.year}-${parts.month}-${parts.day}`;
    }

    monthKeyFromEvent(event) {
      return event.dateKey.slice(0, 7);
    }

    currentMonthKey() {
      return `${this.currentMonth.getUTCFullYear()}-${String(this.currentMonth.getUTCMonth() + 1).padStart(2, '0')}`;
    }

    activeView() {
      return this.mobile ? this.mobileView || 'day' : this.view || 'month';
    }

    mobileLabel(key, values = {}) {
      const defaults = {
        today: 'Heute', week: 'Woche', to_week: 'Zur Wochenansicht wechseln', to_day: 'Zur Tagesansicht wechseln',
        previous_day: 'Vorheriger Tag', next_day: 'Nächster Tag', previous_week: 'Vorherige Woche', next_week: 'Nächste Woche',
        days_label: 'Tage dieser Woche', week_summary: 'Events: [count] · Tag antippen für Details', event_count: 'Events: [count]',
        close: 'Eventdetails schließen', previous_event: 'Vorheriges Event', next_event: 'Nächstes Event', event_position: 'Event [index] von [count]'
      };
      let label = this.mobileLabels?.[key] || defaults[key] || '';
      for (const [name, value] of Object.entries(values)) label = label.replaceAll(`[${name}]`, String(value));
      return label;
    }

    toggleMobileView() {
      if (!this.mobile) return this.showToday();
      const previousUrl = this.monthFeedUrl();
      this.mobileView = this.activeView() === 'day' ? 'week' : 'day';
      this.closeEventDialog();
      if (this.mobileView === 'week') {
        this.selectedDay = this.dateKey(new Date());
        this.currentMonth = new Date(`${this.selectedDay.slice(0, 7)}-01T00:00:00Z`);
      }
      if (previousUrl !== this.monthFeedUrl()) this.loadEvents();
      else this.render();
    }

    selectEventDisplay(display) {
      if (!['scroll', 'compact'].includes(display) || display === this.eventDisplay) return;
      this.eventDisplay = display;
      this.render();
    }

    weekRange(dateKey = this.selectedDay || this.dateKey(new Date())) {
      const start = new Date(`${dateKey}T12:00:00Z`);
      start.setUTCDate(start.getUTCDate() - (start.getUTCDay() + 6) % 7);
      const end = new Date(start);
      end.setUTCDate(end.getUTCDate() + 6);
      return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
    }

    selectView(view) {
      if (!['day', 'week', 'month'].includes(view) || view === this.activeView()) return;
      this.syncSelectedDay(this.filteredEvents());
      const previousUrl = this.monthFeedUrl();
      this.view = view;
      if (previousUrl !== this.monthFeedUrl()) this.loadEvents();
      else this.render();
    }

    changePeriod(offset) {
      const view = this.activeView();
      if (view === 'month') this.changeMonth(offset);
      else this.changeDay(offset * (view === 'week' ? 7 : 1));
    }

    changeMonth(offset) {
      this.currentMonth = new Date(Date.UTC(
        this.currentMonth.getUTCFullYear(),
        this.currentMonth.getUTCMonth() + offset,
        1
      ));
      this.selectedDay = '';
      this.loadEvents();
    }

    showToday() {
      this.pastWeeksExpanded.delete(this.dateKey(new Date()).slice(0, 7));
      this.selectDay(this.dateKey(new Date()));
    }

    togglePastWeeks() {
      const month = this.currentMonthKey();
      if (this.pastWeeksExpanded.has(month)) this.pastWeeksExpanded.delete(month);
      else this.pastWeeksExpanded.add(month);
      this.render();
    }

    setupDayView() {
      if (!this.calendarRegion || this.dayView) return;
      this.dayView = this.element('div', 'eventforge-calendar__day-view');
      this.dayView.setAttribute('data-calendar-day-view', '');
      const navigation = this.element('div', 'eventforge-calendar__day-navigation');
      const button = (text, label, action) => {
        const control = this.element('button', 'eventforge-calendar__button eventforge-calendar__day-button', text);
        control.type = 'button';
        control.setAttribute('aria-label', label);
        control.addEventListener('click', action);
        return control;
      };
      this.dayPrevious = button('‹', 'Vorheriger Tag', () => this.changePeriod(-1));
      this.dayToggle = button('Heute', 'Zur Wochenansicht wechseln', () => this.toggleMobileView());
      this.dayNext = button('›', 'Nächster Tag', () => this.changePeriod(1));
      navigation.append(this.dayPrevious, this.dayToggle, this.dayNext);
      this.dayInput = this.element('input', 'eventforge-calendar__day-input');
      this.dayInput.type = 'date';
      this.dayInput.setAttribute('aria-label', 'Datum auswählen');
      this.dayInput.addEventListener('change', () => this.selectDay(this.dayInput.value));
      this.dayHeading = this.element('h2', 'eventforge-calendar__day-heading');
      this.dayHeading.setAttribute('aria-live', 'polite');
      this.daySummary = this.element('p', 'eventforge-calendar__day-summary');
      this.dayEvents = this.element('div', 'eventforge-calendar__day-agenda');
      this.dayEvents.setAttribute('tabindex', '0');
      this.dayEvents.setAttribute('role', 'region');
      this.dayEvents.setAttribute('aria-label', 'Events für diesen Tag');
      this.mobileWeek = this.element('div', 'eventforge-calendar__mobile-week');
      this.mobileWeek.setAttribute('role', 'group');
      this.mobileWeek.setAttribute('aria-label', this.mobileLabel('days_label'));
      this.mobileWeek.hidden = true;
      this.nextEventButton = button('Zum nächsten Termin', 'Zum nächsten Termin', () => {
        if (this.nextEventDay) this.selectDay(this.nextEventDay);
      });
      this.dayView.append(navigation, this.dayInput, this.dayHeading, this.daySummary, this.mobileWeek, this.dayEvents, this.nextEventButton);
      this.calendarRegion.prepend(this.dayView);
    }

    selectDay(dateKey) {
      const date = /^\d{4}-\d{2}-\d{2}$/.test(dateKey) ? new Date(`${dateKey}T12:00:00Z`) : null;
      if (!date || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== dateKey) {
        if (this.dayInput) this.dayInput.value = this.selectedDay;
        return;
      }
      const previousUrl = this.monthFeedUrl();
      this.selectedDay = dateKey;
      this.currentMonth = new Date(`${dateKey.slice(0, 7)}-01T00:00:00Z`);
      const todayKey = this.dateKey(new Date());
      if (dateKey.slice(0, 7) === todayKey.slice(0, 7) && dateKey < this.weekRange(todayKey).from) {
        this.pastWeeksExpanded.add(this.currentMonthKey());
      }
      if (previousUrl !== this.monthFeedUrl()) this.loadEvents();
      else this.render();
    }

    changeDay(offset) {
      const date = new Date(`${this.selectedDay || this.dateKey(new Date())}T12:00:00Z`);
      date.setUTCDate(date.getUTCDate() + offset);
      this.selectDay(date.toISOString().slice(0, 10));
    }

    syncSelectedDay(monthEvents) {
      if (this.selectedDay?.slice(0, 7) === this.currentMonthKey()) return;
      const todayKey = this.dateKey(new Date());
      this.selectedDay = todayKey.slice(0, 7) === this.currentMonthKey()
        ? todayKey
        : monthEvents.find((event) => !this.isPastEvent(event))?.dateKey || monthEvents[0]?.dateKey || `${this.currentMonthKey()}-01`;
    }

    renderDayView(monthEvents) {
      if (!this.dayView || !this.calendarRegion) return;
      const dayEvents = monthEvents.filter((event) => event.dateKey === this.selectedDay);
      this.dayInput.value = this.selectedDay;
      this.dayHeading.textContent = new Intl.DateTimeFormat('de-DE', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
      }).format(new Date(`${this.selectedDay}T12:00:00Z`));
      this.daySummary.textContent = `${dayEvents.length} ${dayEvents.length === 1 ? 'Event' : 'Events'}`;
      this.dayEvents.replaceChildren();
      dayEvents.forEach((event) => this.dayEvents.append(this.createCalendarEvent(event, true)));
      if (!dayEvents.length) {
        const filtered = this.query || this.filters.location || this.filters.game;
        this.dayEvents.append(this.element('p', 'eventforge-calendar__day-empty', filtered
          ? 'Keine passenden Events an diesem Tag.' : 'Für diesen Tag sind keine Events geplant.'));
      }
      this.nextEventDay = monthEvents.map((event) => event.dateKey).filter((date) => date > this.selectedDay).sort()[0] || '';
      this.nextEventButton.hidden = dayEvents.length > 0 || !this.nextEventDay;
      const view = this.activeView();
      const mobileWeek = this.mobile && view === 'week';
      this.dayView.hidden = view !== 'day' && !mobileWeek;
      this.dayInput.hidden = mobileWeek;
      this.dayEvents.hidden = mobileWeek;
      this.mobileWeek.hidden = !mobileWeek;
      this.dayToggle.textContent = this.mobileLabel(mobileWeek ? 'week' : 'today');
      this.dayToggle.setAttribute('aria-label', this.mobile ? this.mobileLabel(mobileWeek ? 'to_day' : 'to_week') : this.mobileLabel('today'));
      this.dayToggle.setAttribute('aria-pressed', String(mobileWeek));
      this.dayPrevious.setAttribute('aria-label', this.mobileLabel(mobileWeek ? 'previous_week' : 'previous_day'));
      this.dayNext.setAttribute('aria-label', this.mobileLabel(mobileWeek ? 'next_week' : 'next_day'));
      if (mobileWeek) {
        const week = this.weekRange();
        const weekEvents = monthEvents.filter((event) => event.dateKey >= week.from && event.dateKey <= week.to);
        this.dayHeading.textContent = this.monthLabel.textContent;
        this.daySummary.textContent = this.mobileLabel('week_summary', { count: weekEvents.length });
        this.nextEventButton.hidden = true;
        this.renderMobileWeek(weekEvents, week);
      }
      this.calendarRegion.setAttribute('aria-label', { day: 'Tageskalender', week: 'Wochenkalender', month: 'Monatskalender' }[view]);
      this.calendarRegion.setAttribute('tabindex', this.mobile ? '-1' : '0');
    }

    renderMobileWeek(events, { from }) {
      const date = new Date(`${from}T12:00:00Z`);
      this.mobileWeek.replaceChildren();
      for (let index = 0; index < 7; index += 1) {
        const key = date.toISOString().slice(0, 10);
        const dayEvents = events.filter((event) => event.dateKey === key);
        const button = this.element('button', 'eventforge-calendar__week-day');
        button.type = 'button';
        button.dataset.date = key;
        button.disabled = dayEvents.length === 0;
        button.classList.toggle('eventforge-calendar__week-day--events', dayEvents.length > 0);
        if (key === this.dateKey(new Date())) button.setAttribute('aria-current', 'date');
        const label = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
        button.setAttribute('aria-label', `${label} · ${this.mobileLabel('event_count', { count: dayEvents.length })}`);
        button.append(
          this.element('span', 'eventforge-calendar__week-weekday', new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: 'UTC' }).format(date).replace('.', '')),
          this.element('strong', 'eventforge-calendar__week-date', String(date.getUTCDate())),
          this.element('span', 'eventforge-calendar__week-count', dayEvents.length ? String(dayEvents.length) : '–')
        );
        button.addEventListener('click', () => this.openDayEvents(key, button));
        this.mobileWeek.append(button);
        date.setUTCDate(date.getUTCDate() + 1);
      }
    }

    openDayEvents(day, trigger) {
      if (!this.mobile || this.activeView() !== 'week') return;
      this.popupDay = day;
      this.popupEventId = null;
      if (!this.popupEvents().length) return;
      this.popupTrigger = trigger;
      this.ensureEventDialog();
      this.renderEventDialog();
      if (typeof this.eventDialog.showModal === 'function') {
        if (!this.eventDialog.open) this.eventDialog.showModal();
      } else this.eventDialog.setAttribute('open', '');
      document.documentElement.classList.add('eventforge-event-open');
    }

    popupEvents() {
      return this.filteredEvents().filter((event) => event.dateKey === this.popupDay).sort((a, b) => a.date - b.date);
    }

    closeEventDialog() {
      if (!this.eventDialog) return;
      if (this.eventDialog.open && typeof this.eventDialog.close === 'function') this.eventDialog.close();
      else this.eventDialog.removeAttribute('open');
      document.documentElement.classList.remove('eventforge-event-open');
      this.restorePopupFocus();
    }

    restorePopupFocus() {
      const day = Array.from(this.mobileWeek?.querySelectorAll('button') || []).find((button) => button.dataset.date === this.popupDay);
      (day && !day.disabled ? day : this.dayToggle || this.popupTrigger)?.focus?.();
    }

    ensureEventDialog() {
      if (this.eventDialog) return;
      const dialog = this.element('dialog', 'eventforge-calendar__event-dialog');
      const titleId = `EventforgeEventTitle-${Math.random().toString(36).slice(2, 10)}`;
      dialog.setAttribute('aria-labelledby', titleId);
      const toolbar = this.element('div', 'eventforge-calendar__popup-toolbar');
      this.popupHeading = this.element('h2', 'eventforge-calendar__popup-heading');
      this.popupHeading.id = titleId;
      const close = this.element('button', 'eventforge-calendar__popup-button', '×');
      close.type = 'button';
      close.setAttribute('aria-label', this.mobileLabel('close'));
      close.addEventListener('click', () => this.closeEventDialog());
      toolbar.append(this.popupHeading, close);
      this.popupContent = this.element('div', 'eventforge-calendar__popup-content');
      this.popupContent.setAttribute('aria-live', 'polite');
      const navigation = this.element('div', 'eventforge-calendar__popup-navigation');
      this.popupPrevious = this.element('button', 'eventforge-calendar__popup-button', '‹');
      this.popupNext = this.element('button', 'eventforge-calendar__popup-button', '›');
      this.popupCounter = this.element('span', 'eventforge-calendar__popup-counter');
      this.popupCounter.setAttribute('aria-live', 'polite');
      for (const [button, offset, label] of [[this.popupPrevious, -1, this.mobileLabel('previous_event')], [this.popupNext, 1, this.mobileLabel('next_event')]]) {
        button.type = 'button';
        button.setAttribute('aria-label', label);
        button.addEventListener('click', () => this.stepPopup(offset));
      }
      navigation.append(this.popupPrevious, this.popupCounter, this.popupNext);
      dialog.append(toolbar, this.popupContent, navigation);
      dialog.addEventListener('click', (event) => { if (event.target === dialog) this.closeEventDialog(); });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('eventforge-event-open');
        this.restorePopupFocus();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.stepPopup(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      this.popupContent.addEventListener('pointerdown', (event) => {
        this.popupPointer = event.pointerType === 'touch' && event.isPrimary !== false ? { x: event.clientX, y: event.clientY, id: event.pointerId } : null;
      });
      this.popupContent.addEventListener('pointercancel', () => { this.popupPointer = null; });
      this.popupContent.addEventListener('pointerup', (event) => {
        const start = this.popupPointer;
        this.popupPointer = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          this.suppressPopupClickUntil = Date.now() + 350;
          this.stepPopup(dx < 0 ? 1 : -1);
        }
      });
      this.popupContent.addEventListener('click', (event) => {
        if (Date.now() < this.suppressPopupClickUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      this.append(dialog);
      this.eventDialog = dialog;
    }

    stepPopup(offset) {
      const events = this.popupEvents();
      if (!events.length) return this.closeEventDialog();
      const current = Math.max(0, events.findIndex((event) => event.id === this.popupEventId));
      this.popupEventId = events[(current + offset + events.length) % events.length].id;
      this.renderEventDialog();
    }

    renderEventDialog() {
      if (!this.eventDialog) return;
      const events = this.popupEvents();
      if (!events.length || !this.mobile || this.activeView() !== 'week') return this.closeEventDialog();
      const index = Math.max(0, events.findIndex((event) => event.id === this.popupEventId));
      const event = events[index];
      this.popupEventId = event.id;
      this.popupHeading.textContent = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${this.popupDay}T12:00:00Z`));
      this.popupCounter.textContent = this.mobileLabel('event_position', { index: index + 1, count: events.length });
      this.popupPrevious.disabled = this.popupNext.disabled = events.length < 2;
      const card = this.element('article', 'eventforge-calendar__card');
      const content = this.element('div', 'eventforge-calendar__card-content');
      content.append(this.createCardHeader(event), this.element('h3', 'eventforge-calendar__card-title', event.title));
      const meta = [event.location, ...event.games, ...event.formats].filter(Boolean).join(' · ');
      if (meta) content.append(this.element('p', 'eventforge-calendar__card-meta', meta));
      if (event.description) content.append(this.element('p', 'eventforge-calendar__card-description', event.description));
      if (!this.isPastEvent(event)) {
        const facts = this.createCardFacts(event);
        if (facts) content.append(facts);
      }
      if (!this.isRunningEvent(event)) content.append(this.eventAction(event));
      card.append(content);
      this.popupContent.replaceChildren(card);
    }

    handleFilterClick(event) {
      const button = event.target.closest('[data-filter-type][data-filter-value]');
      if (!button) return;
      this.filters[button.dataset.filterType] = button.dataset.filterValue;
      this.renderFilters();
      this.render();
    }

    renderFilters() {
      if (!this.filterContainer) return;
      this.filterContainer.replaceChildren();
      const groups = [
        {
          type: 'location',
          label: 'Laden',
          values: this.events.map((event) => event.location).filter(Boolean),
          display: (value) => this.shortLocation(value)
        },
        {
          type: 'game',
          label: 'Spiel',
          values: this.events.flatMap((event) => event.games),
          display: (value) => this.translate(value)
        }
      ];

      groups.forEach(({ type, label, values: groupValues, display }) => {
        const values = [...new Set(groupValues)]
          .sort((a, b) => a.localeCompare(b, 'de'));
        if (!values.length) return;

        const group = this.element('div', 'eventforge-calendar__filter-group');
        const groupLabel = this.element('span', 'eventforge-calendar__filter-label', `${label}:`);
        group.append(groupLabel, this.filterButton(type, '', 'Alle'));
        values.forEach((value) => group.append(this.filterButton(type, value, display(value))));
        this.filterContainer.append(group);
      });
    }

    filterButton(type, value, label) {
      const button = this.element('button', 'eventforge-calendar__filter-button', label);
      button.type = 'button';
      button.dataset.filterType = type;
      button.dataset.filterValue = value;
      button.setAttribute('aria-pressed', String(this.filters[type] === value));
      return button;
    }

    filteredEvents() {
      return this.events.filter((event) => {
        const searchable = [event.title, event.description, event.location, ...event.games, ...event.formats]
          .join(' ')
          .toLocaleLowerCase('de-DE');
        const matchesQuery = !this.query || searchable.includes(this.query);
        const matchesLocation = !this.filters.location || event.location === this.filters.location;
        const matchesGame = !this.filters.game || event.games.includes(this.filters.game);
        return matchesQuery && matchesLocation && matchesGame;
      });
    }

    shortLocation(value) {
      return String(value || '').replace(/^Spielewelt\s+/i, '').trim() || value;
    }

    destinationUrl(event) {
      if (this.eventState(event) !== 'upcoming' || event.soldOut) return '';
      return this.shopifyTicketUrl(event.shopifyUrl);
    }

    isFreeRsvp(event) {
      return event.isFree === true && !event.shopifyProductId && !event.hasPaidPricing &&
        ['', 'free', 'no_registration'].includes(event.paymentMode || '');
    }

    canRegisterFree(event) {
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!this.isFreeRsvp(event) || event.freeRegistrationSupported !== true ||
        this.eventState(event) !== 'upcoming' || event.soldOut || event.registrationRequired === false ||
        event.paymentMode !== 'free' || !uuid.test(event.eventforgeId || '') || !uuid.test(event.organizationId || '')) return false;
      return !Number.isFinite(event.registrationCutoffMinutes) ||
        Date.now() < event.date.getTime() - event.registrationCutoffMinutes * 60000;
    }

    calendarEventState(event) {
      return `${this.eventState(event)}:${this.canRegisterFree(event)}`;
    }

    shopifyTicketUrl(value) {
      const safeUrl = this.safeUrl(value);
      if (!safeUrl) return '';
      const url = new URL(safeUrl);
      const storefront = new URL(window.location.origin);
      return url.origin === storefront.origin && /\/products\/[^/]+\/?$/.test(url.pathname) ? url.href : '';
    }

    eventState(event) {
      if (this.isPastEvent(event)) return 'past';
      return this.isRunningEvent(event) ? 'running' : 'upcoming';
    }

    isRunningEvent(event) {
      const now = Date.now();
      return Boolean(event.endDate && event.date.getTime() <= now && now < event.endDate.getTime());
    }

    isPastEvent(event) {
      return (event.endDate || event.date).getTime() <= Date.now();
    }

    canJoinWaitlist(event) {
      return Boolean(
        this.eventState(event) === 'upcoming' &&
        event.soldOut &&
        event.waitlistEnabled === true &&
        event.eventforgeId &&
        event.organizationId
      );
    }

    render() {
      if (this.getAttribute('aria-busy') === 'true') return;
      if (!this.monthLabel || !this.grid || !this.cards) return;
      const monthName = new Intl.DateTimeFormat('de-DE', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
      }).format(this.currentMonth);
      const filtered = this.filteredEvents();
      const monthEvents = filtered.filter((event) => this.monthKeyFromEvent(event) === this.currentMonthKey());
      this.syncSelectedDay(monthEvents);
      const view = this.activeView();
      const week = this.weekRange();
      const viewEvents = view === 'day' ? filtered.filter((event) => event.dateKey === this.selectedDay)
        : view === 'week' ? filtered.filter((event) => event.dateKey >= week.from && event.dateKey <= week.to) : monthEvents;
      const formatDate = (key) => new Intl.DateTimeFormat('de-DE', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
      }).format(new Date(`${key}T12:00:00Z`));
      this.monthLabel.textContent = view === 'day' ? formatDate(this.selectedDay)
        : view === 'week' ? `${formatDate(week.from)} – ${formatDate(week.to)}` : monthName;
      this.listHeading.textContent = view === 'day' ? 'Tickets & Infos für diesen Tag'
        : view === 'week' ? 'Tickets & Infos für diese Woche' : `Termine im ${monthName}`;
      this.dataset.calendarActiveView = view;
      this.dataset.calendarDisplay = this.eventDisplay;
      this.querySelectorAll('[data-calendar-display]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.calendarDisplay === this.eventDisplay));
      });
      const displaySwitcher = this.querySelector('[data-calendar-display-switcher]');
      if (displaySwitcher) displaySwitcher.hidden = view === 'day';
      const monthFocus = this.querySelector('[data-calendar-month-focus]');
      if (monthFocus) monthFocus.hidden = view !== 'month';
      this.querySelectorAll('[data-calendar-view]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.calendarView === view));
      });
      const period = { day: ['Vorheriger Tag', 'Nächster Tag'], week: ['Vorherige Woche', 'Nächste Woche'], month: ['Vorheriger Monat', 'Nächster Monat'] }[view];
      this.querySelector('[data-calendar-previous]')?.setAttribute('aria-label', period[0]);
      this.querySelector('[data-calendar-next]')?.setAttribute('aria-label', period[1]);
      this.grid.hidden = view === 'day';
      const weekdays = this.calendarRegion?.querySelector('.eventforge-calendar__weekdays');
      if (weekdays) weekdays.hidden = view === 'day';
      if (view === 'week') this.renderWeekGrid(viewEvents, week);
      else this.renderMonthGrid(monthEvents);
      this.renderDayView(filtered);
      this.renderCards(viewEvents);
      if (this.mobile && view === 'week') {
        this.cards.hidden = true;
        this.listHeading.hidden = true;
      }
      if (this.eventDialog?.open) this.renderEventDialog();
      this.cards.setAttribute('tabindex', '0');
      this.cards.setAttribute('role', 'region');
      this.cards.setAttribute('aria-label', this.listHeading.textContent);
      this.renderedEventState = this.events.map((event) => this.calendarEventState(event)).join(',');
    }

    renderWeekGrid(events, { from }) {
      this.grid.replaceChildren();
      const date = new Date(`${from}T12:00:00Z`);
      for (let index = 0; index < 7; index += 1) {
        const key = date.toISOString().slice(0, 10);
        this.grid.append(this.createCalendarDay(key, events.filter((event) => event.dateKey === key), true));
        date.setUTCDate(date.getUTCDate() + 1);
      }
    }

    renderMonthGrid(monthEvents) {
      this.grid.replaceChildren();
      const year = this.currentMonth.getUTCFullYear();
      const month = this.currentMonth.getUTCMonth();
      const firstWeekday = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      const cellCount = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
      const todayKey = this.dateKey(new Date());
      const currentWeekRow = this.currentMonthKey() === todayKey.slice(0, 7)
        ? Math.floor((firstWeekday + Number(todayKey.slice(8)) - 1) / 7) : 0;
      const expanded = this.pastWeeksExpanded.has(this.currentMonthKey());
      const focus = this.querySelector('[data-calendar-month-focus]');
      const toggle = this.querySelector('[data-calendar-past-weeks]');
      if (focus) focus.hidden = this.activeView() !== 'month' || currentWeekRow === 0;
      if (toggle) {
        toggle.textContent = expanded ? toggle.dataset.labelHide : toggle.dataset.labelShow;
        toggle.setAttribute('aria-expanded', String(expanded));
      }
      const focusLabel = this.querySelector('[data-calendar-focus-label]');
      if (focusLabel) focusLabel.hidden = expanded;
      const firstCell = expanded ? 0 : currentWeekRow * 7;

      for (let cellIndex = firstCell; cellIndex < cellCount; cellIndex += 1) {
        const dayNumber = cellIndex - firstWeekday + 1;
        if (dayNumber < 1 || dayNumber > daysInMonth) {
          const emptyCell = this.element('div', 'eventforge-calendar__day eventforge-calendar__day--empty');
          emptyCell.setAttribute('aria-hidden', 'true');
          this.grid.append(emptyCell);
          continue;
        }

        const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`;
        const dayEvents = monthEvents.filter((event) => event.dateKey === dateKey);
        this.grid.append(this.createCalendarDay(dateKey, dayEvents));
      }
    }

    createCalendarDay(dateKey, events, week = false) {
      const date = new Date(`${dateKey}T12:00:00Z`);
      const day = this.element('div', 'eventforge-calendar__day');
      if (dateKey === this.dateKey(new Date())) day.classList.add('eventforge-calendar__day--today');
      const currentWeek = this.weekRange(this.dateKey(new Date()));
      if (dateKey >= currentWeek.from && dateKey <= currentWeek.to) day.classList.add('eventforge-calendar__day--current-week');
      const dayLabel = new Intl.DateTimeFormat('de-DE', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
      }).format(date);
      day.setAttribute('aria-label', dayLabel);
      const heading = this.element('div', 'eventforge-calendar__day-header');
      heading.append(this.element('span', 'eventforge-calendar__day-number', week
        ? `${date.getUTCDate()}.${date.getUTCMonth() + 1}.` : String(date.getUTCDate())));
      day.append(heading);
      const eventList = this.element('div', 'eventforge-calendar__day-events');
      if (events.length) eventList.setAttribute('tabindex', '0');
      eventList.setAttribute('role', 'region');
      eventList.setAttribute('aria-label', `${dayLabel}: ${events.length} Events`);
      if (this.eventDisplay === 'compact' && events.length) {
        const navigation = this.element('div', 'eventforge-calendar__event-navigation');
        const counter = this.element('span', 'eventforge-calendar__event-counter');
        let index = Math.max(0, events.findIndex((event) => event.id === this.dayEventSelection.get(dateKey)));
        const update = () => {
          this.dayEventSelection.set(dateKey, events[index].id);
          eventList.replaceChildren(this.createCalendarEvent(events[index], true));
          counter.textContent = `${index + 1}/${events.length}`;
          counter.setAttribute('aria-label', `Event ${index + 1} von ${events.length}`);
        };
        const arrow = (text, label, offset) => {
          const button = this.element('button', 'eventforge-calendar__event-arrow', text);
          button.type = 'button';
          button.setAttribute('aria-label', `${label}: ${dayLabel}`);
          button.addEventListener('click', () => {
            index = (index + offset + events.length) % events.length;
            update();
          });
          return button;
        };
        eventList.setAttribute('aria-live', 'polite');
        eventList.setAttribute('aria-atomic', 'true');
        if (events.length > 1) navigation.append(arrow('‹', 'Vorheriges Event', -1));
        navigation.append(counter);
        if (events.length > 1) navigation.append(arrow('›', 'Nächstes Event', 1));
        heading.append(navigation);
        update();
      } else {
        events.forEach((event) => eventList.append(this.createCalendarEvent(event, week)));
      }
      day.append(eventList);
      return day;
    }

    createCalendarEvent(event, expanded = false) {
      const past = this.isPastEvent(event);
      const running = this.isRunningEvent(event);
      const waitlist = this.canJoinWaitlist(event);
      const destination = this.destinationUrl(event);
      const game = event.games.map((value) => this.translate(value))[0] || 'Event';
      const location = this.shortLocation(event.location);
      const link = this.element(waitlist ? 'button' : destination ? 'a' : 'div', 'eventforge-calendar__event-link');
      if (past) link.classList.add('eventforge-calendar__event-link--past');
      if (this.layout === 'shop' && !expanded) {
        link.textContent = `${this.formatEventTime(event)} · ${game}`;
      } else {
        link.append(
          this.element('span', 'eventforge-calendar__event-meta', `${this.formatEventTime(event)} · ${game}`),
          this.element('span', 'eventforge-calendar__event-title', event.title)
        );
      }
      const footer = this.element('div', 'eventforge-calendar__event-footer');
      if ((this.layout !== 'shop' || expanded) && location) {
        footer.append(this.element('span', 'eventforge-calendar__event-location', location));
      }
      const indicators = this.element('span', 'eventforge-calendar__event-indicators');
      const count = Number.isFinite(event.confirmedCount) ? event.confirmedCount
        : Number.isFinite(event.remainingSlots) ? event.capacity - event.remainingSlots : null;
      const hasCount = Number.isFinite(event.capacity) && event.capacity > 0 && Number.isFinite(count) && count >= 0;
      if (hasCount) {
        const registration = this.element('span', 'eventforge-calendar__registration-count', `${count}/${event.capacity}`);
        registration.classList.toggle('eventforge-calendar__registration-count--empty', count === 0);
        registration.classList.toggle('eventforge-calendar__registration-count--limited', count > 0 && count < event.capacity && count / event.capacity >= 0.75);
        registration.classList.toggle('eventforge-calendar__registration-count--full', count >= event.capacity);
        registration.setAttribute('aria-label', `${count} von ${event.capacity} Plätzen besetzt`);
        indicators.append(registration);
      }
      if (past || running) {
        indicators.append(this.element('span', past ? 'eventforge-calendar__past-label' : 'eventforge-calendar__running-label', past ? 'Vergangen' : 'Laufend'));
      }
      if (indicators.children.length) footer.append(indicators);
      if (footer.children.length) link.append(footer);
      if (!past && !running && destination) {
        link.href = destination;
        link.addEventListener('click', (clickEvent) => {
          if (this.eventState(event) !== 'upcoming') {
            clickEvent.preventDefault();
            this.render();
          }
        });
      }
      if (waitlist) {
        link.type = 'button';
        link.dataset.waitlistAction = 'true';
        link.addEventListener('click', (clickEvent) => {
          clickEvent.preventDefault();
          this.openWaitlist(event);
        });
      }
      link.title = [this.formatEventTime(event), event.title, game, location, hasCount ? `${count} von ${event.capacity} Plätzen besetzt` : '', past ? 'Vergangen' : running ? 'Laufend' : ''].filter(Boolean).join(' – ');
      link.setAttribute('aria-label', link.title);
      link.dataset.calendarLocation = this.locationKey(event.location);
      return link;
    }

    renderCards(monthEvents) {
      this.cards.replaceChildren();
      const cardEvents = monthEvents.filter((event) => !this.isPastEvent(event));
      this.cards.hidden = !cardEvents.length;
      if (this.listHeading) this.listHeading.hidden = !cardEvents.length;
      if (!cardEvents.length) return;

      if (this.layout === 'shop') {
        this.renderShopCards(cardEvents);
        return;
      }

      cardEvents.forEach((event) => {
        const card = this.element('article', 'eventforge-calendar__card');
        if (event.image) {
          const imageWrap = this.element('div', 'eventforge-calendar__card-image-wrap');
          const image = this.element('img', 'eventforge-calendar__card-image');
          image.src = event.image;
          image.alt = '';
          image.loading = 'lazy';
          image.addEventListener('error', () => imageWrap.remove(), { once: true });
          imageWrap.append(image);
          card.append(imageWrap);
        }

        const content = this.element('div', 'eventforge-calendar__card-content');
        content.append(this.createCardHeader(event));
        content.append(this.element('h4', 'eventforge-calendar__card-title', event.title));
        if (event.description) content.append(this.element('p', 'eventforge-calendar__card-description', event.description));

        const meta = [
          event.location,
          event.games.map((game) => this.translate(game)).join(', '),
          event.formats.map((format) => this.translate(format)).join(', ')
        ].filter(Boolean);
        if (meta.length) content.append(this.element('p', 'eventforge-calendar__card-meta', meta.join(' · ')));

        const facts = this.createCardFacts(event);
        if (facts) content.append(facts);
        if (!this.isRunningEvent(event)) content.append(this.eventAction(event));
        card.append(content);
        this.cards.append(card);
      });
    }

    renderShopCards(monthEvents) {
      monthEvents.forEach((event) => {
        const card = this.element('article', 'eventforge-calendar__card eventforge-calendar__card--compact');
        card.dataset.calendarLocation = this.locationKey(event.location);

        const dateParts = this.compactDateParts(event.date);
        const dateTile = this.element('div', 'eventforge-calendar__date-tile');
        dateTile.append(
          this.element('span', 'eventforge-calendar__date-month', dateParts.month),
          this.element('strong', 'eventforge-calendar__date-day', dateParts.day)
        );

        const content = this.element('div', 'eventforge-calendar__card-content');
        content.append(this.createCardHeader(event, true));
        content.append(this.element('h4', 'eventforge-calendar__card-title', event.title));

        const meta = [
          this.shortLocation(event.location),
          event.games.map((game) => this.translate(game)).join(', '),
          event.formats.map((format) => this.translate(format)).join(', ')
        ].filter(Boolean);
        if (meta.length) content.append(this.element('p', 'eventforge-calendar__card-meta', meta.join(' · ')));

        const facts = this.createCardFacts(event);
        if (facts) content.append(facts);
        if (!this.isRunningEvent(event)) content.append(this.eventAction(event));

        card.append(dateTile, content);
        this.cards.append(card);
      });
    }

    createCardHeader(event, compact = false) {
      const date = this.element('p', 'eventforge-calendar__card-date', this.formatEventDateTime(event, compact));
      if (!this.isRunningEvent(event)) return date;
      const header = this.element('div', 'eventforge-calendar__card-header');
      header.append(date, this.eventAction(event));
      return header;
    }

    compactDateParts(date) {
      const parts = new Intl.DateTimeFormat('de-DE', {
        timeZone: this.timeZone,
        day: '2-digit',
        month: 'short'
      }).formatToParts(date);
      const values = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
      return {
        day: values.day || '',
        month: String(values.month || '').replace('.', '').toLocaleUpperCase('de-DE')
      };
    }

    formatCompactDateTime(date) {
      return new Intl.DateTimeFormat('de-DE', {
        timeZone: this.timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date).replace(',', ' ·').replace('.', '');
    }

    locationKey(value) {
      return String(this.shortLocation(value) || '')
        .toLocaleLowerCase('de-DE')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-');
    }

    createCardFacts(event) {
      const meter = this.createAvailabilityMeter(event);
      if (!event.price && !meter) return null;
      const facts = this.element('div', 'eventforge-calendar__card-facts');
      if (event.price) {
        facts.append(this.element('span', 'eventforge-calendar__card-fact eventforge-calendar__card-fact--price', event.price));
      }
      if (meter) facts.append(meter);
      return facts;
    }

    createAvailabilityMeter(event) {
      if (this.isPastEvent(event)) return null;
      if (!Number.isFinite(event.capacity) || event.capacity <= 0) {
        return null;
      }
      const count = Number.isFinite(event.confirmedCount)
        ? event.confirmedCount
        : Number.isFinite(event.remainingSlots) ? event.capacity - event.remainingSlots : null;
      if (!Number.isFinite(count)) return null;
      const occupied = Math.min(event.capacity, Math.max(0, count));
      const meter = this.element('div', 'eventforge-calendar__availability-meter');
      meter.classList.toggle('eventforge-calendar__availability-meter--empty', occupied === 0);
      meter.setAttribute('role', 'meter');
      meter.setAttribute('aria-label', `Besetzte Plätze: ${event.title}`);
      meter.setAttribute('aria-valuemin', '0');
      meter.setAttribute('aria-valuemax', String(event.capacity));
      meter.setAttribute('aria-valuenow', String(occupied));
      meter.setAttribute('aria-valuetext', `${occupied} von ${event.capacity} Plätzen besetzt`);

      const fill = this.element('span', 'eventforge-calendar__availability-fill');
      fill.style.width = `${occupied / event.capacity * 100}%`;
      fill.setAttribute('aria-hidden', 'true');
      const label = this.element('span', 'eventforge-calendar__availability-label', `${occupied} / ${event.capacity} besetzt`);
      label.setAttribute('aria-hidden', 'true');
      meter.append(fill, label);
      return meter;
    }

    eventAction(event) {
      if (this.isPastEvent(event)) {
        return this.element('span', 'eventforge-calendar__past-status', 'Vergangen · Anmeldung geschlossen');
      }
      if (this.isRunningEvent(event)) {
        const status = this.element('span', 'eventforge-calendar__running-status', 'Laufend');
        status.setAttribute('role', 'status');
        return status;
      }
      if (this.canJoinWaitlist(event)) {
        const button = this.element('button', 'eventforge-calendar__card-link', 'Auf die Warteliste');
        button.type = 'button';
        button.setAttribute('aria-label', `Auf die Warteliste: ${event.title}`);
        button.addEventListener('click', () => this.openWaitlist(event));
        return button;
      }

      if (this.isFreeRsvp(event) && !event.soldOut) {
        const action = this.element('div', 'eventforge-calendar__rsvp');
        const canRegister = this.canRegisterFree(event);
        const noRegistration = !canRegister;
        const notice = noRegistration
          ? 'Komm einfach vorbei! Keine Anmeldung nötig.'
          : 'Komm einfach vorbei! Die Anmeldung ist freiwillig.';
        action.append(this.element('p', 'eventforge-calendar__rsvp-notice', notice));
        if (Number.isFinite(event.capacity) && event.capacity > 0) {
          action.append(this.element('p', 'eventforge-calendar__rsvp-detail', 'Die Plätze vor Ort sind begrenzt.'));
        }
        if (canRegister) {
          const button = this.element('button', 'eventforge-calendar__card-link eventforge-calendar__rsvp-button', 'Freiwillig anmelden');
          button.type = 'button';
          button.dataset.rsvpEvent = event.eventforgeId;
          button.setAttribute('aria-label', `Freiwillig anmelden: ${event.title}`);
          button.addEventListener('click', () => this.openFreeRegistration(event, button));
          action.append(button);
        }
        return action;
      }

      const destination = this.destinationUrl(event);
      if (!destination) {
        return this.element('span', 'eventforge-calendar__ticket-status', event.soldOut ? 'Ausverkauft' : 'Ticket nicht verfügbar');
      }
      const linkLabel = 'Ticket kaufen';
      const link = this.element('a', 'eventforge-calendar__card-link', linkLabel);
      link.href = destination;
      link.setAttribute('aria-label', `${linkLabel}: ${event.title}`);
      link.addEventListener('click', (clickEvent) => {
        if (this.eventState(event) !== 'upcoming') {
          clickEvent.preventDefault();
          this.render();
        }
      });
      return link;
    }

    ensureFreeRegistrationDialog() {
      if (this.freeRegistrationDialog?.isConnected) return this.freeRegistrationDialog;
      const dialog = document.createElement('dialog');
      const titleId = `EventforgeRsvpTitle-${Math.random().toString(36).slice(2, 10)}`;
      dialog.className = 'eventforge-waitlist-dialog';
      dialog.setAttribute('aria-labelledby', titleId);
      dialog.innerHTML = `
        <div class="eventforge-waitlist-dialog__panel">
          <button class="eventforge-waitlist-dialog__close" type="button" data-rsvp-close aria-label="Anmeldung schließen">×</button>
          <div data-rsvp-entry>
            <p class="eventforge-waitlist-dialog__eyebrow">Freiwillige Anmeldung</p>
            <h2 class="eventforge-waitlist-dialog__title" id="${titleId}" data-rsvp-title></h2>
            <p class="eventforge-waitlist-dialog__meta" data-rsvp-meta></p>
            <p class="eventforge-waitlist-dialog__intro">Die Teilnahme ist kostenlos. Du kannst auch ohne Anmeldung einfach vorbeikommen.</p>
            <form class="eventforge-waitlist-form" data-rsvp-form>
              <div class="eventforge-waitlist-form__row">
                <label class="eventforge-waitlist-form__field">
                  <span>Vorname</span>
                  <input name="first_name" type="text" autocomplete="given-name" maxlength="50" required>
                </label>
                <label class="eventforge-waitlist-form__field">
                  <span>Nachname</span>
                  <input name="last_name" type="text" autocomplete="family-name" maxlength="50" required>
                </label>
              </div>
              <label class="eventforge-waitlist-form__field">
                <span>E-Mail-Adresse</span>
                <input name="email" type="email" autocomplete="email" maxlength="255" required>
              </label>
              <p class="eventforge-waitlist-form__privacy">Deine Angaben werden für die Eventanmeldung und die zugehörige Bestätigung verwendet.</p>
              <p class="eventforge-waitlist-form__error" data-rsvp-error role="alert" hidden></p>
              <button class="eventforge-waitlist-form__submit" type="submit" data-rsvp-submit>Kostenlos anmelden</button>
            </form>
          </div>
          <div class="eventforge-waitlist-success" data-rsvp-success role="status" hidden>
            <span class="eventforge-waitlist-success__icon" aria-hidden="true">✓</span>
            <h2>Du bist angemeldet!</h2>
            <p data-rsvp-success-text></p>
            <button class="eventforge-waitlist-form__submit" type="button" data-rsvp-done>Fertig</button>
          </div>
        </div>
      `;
      const close = () => this.closeFreeRegistration();
      dialog.querySelector('[data-rsvp-close]')?.addEventListener('click', close);
      dialog.querySelector('[data-rsvp-done]')?.addEventListener('click', close);
      dialog.querySelector('[data-rsvp-form]')?.addEventListener('submit', (event) => this.submitFreeRegistration(event));
      dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
      dialog.addEventListener('close', () => { if (!dialog.open) close(); });
      document.body.append(dialog);
      this.freeRegistrationDialog = dialog;
      return dialog;
    }

    openFreeRegistration(event, trigger) {
      if (!this.canRegisterFree(event)) {
        this.render();
        return;
      }
      this.freeRegistrationGeneration += 1;
      this.freeRegistrationEvent = event;
      this.freeRegistrationTrigger = trigger;
      const dialog = this.ensureFreeRegistrationDialog();
      const form = dialog.querySelector('[data-rsvp-form]');
      form?.reset();
      dialog.querySelector('[data-rsvp-entry]').hidden = false;
      dialog.querySelector('[data-rsvp-success]').hidden = true;
      const error = dialog.querySelector('[data-rsvp-error]');
      error.hidden = true;
      error.textContent = '';
      dialog.querySelector('[data-rsvp-title]').textContent = event.title;
      dialog.querySelector('[data-rsvp-meta]').textContent = [this.formatDateTime(event.date), this.shortLocation(event.location)].filter(Boolean).join(' · ');
      if (form) {
        form.elements.first_name.value = this.customer?.firstName || '';
        form.elements.last_name.value = this.customer?.lastName || '';
        form.elements.email.value = this.customer?.email || '';
      }
      const submit = dialog.querySelector('[data-rsvp-submit]');
      submit.disabled = Boolean(this.freeRegistrationPending);
      submit.textContent = this.freeRegistrationPending ? 'Anmeldung wird geprüft …' : 'Kostenlos anmelden';
      document.documentElement.classList.add('eventforge-rsvp-open');
      if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
      else if (!dialog.open) dialog.setAttribute('open', '');
      requestAnimationFrame(() => {
        Array.from(form?.elements || []).find((field) => field.tagName === 'INPUT' && !field.value)?.focus();
      });
    }

    closeFreeRegistration() {
      const dialog = this.freeRegistrationDialog;
      this.freeRegistrationGeneration += 1;
      this.freeRegistrationEvent = null;
      document.documentElement.classList.remove('eventforge-rsvp-open');
      if (dialog?.open && typeof dialog.close === 'function') dialog.close();
      else dialog?.removeAttribute('open');
      if (this.freeRegistrationTrigger?.isConnected) this.freeRegistrationTrigger.focus();
      this.freeRegistrationTrigger = null;
    }

    async submitFreeRegistration(submitEvent) {
      submitEvent.preventDefault();
      const event = this.freeRegistrationEvent;
      const dialog = this.freeRegistrationDialog;
      const form = submitEvent.currentTarget;
      if (this.freeRegistrationPending || !event || !dialog) return;
      const error = dialog.querySelector('[data-rsvp-error]');
      error.hidden = true;
      error.textContent = '';
      if (!this.canRegisterFree(event)) {
        error.textContent = 'Die Online-Anmeldung ist für dieses Event nicht mehr verfügbar. Du kannst weiterhin vorbeikommen.';
        error.hidden = false;
        return;
      }
      if (!form.reportValidity()) return;
      const firstName = form.elements.first_name.value.trim();
      const lastName = form.elements.last_name.value.trim();
      const email = form.elements.email.value.trim().toLowerCase();
      if (!firstName || !lastName || !email) {
        error.textContent = 'Bitte fülle Vorname, Nachname und E-Mail-Adresse aus.';
        error.hidden = false;
        return;
      }
      const pending = { generation: this.freeRegistrationGeneration, eventId: event.eventforgeId };
      this.freeRegistrationPending = pending;
      const submit = dialog.querySelector('[data-rsvp-submit]');
      submit.disabled = true;
      submit.textContent = 'Wird angemeldet …';
      form.setAttribute('aria-busy', 'true');
      const current = () => this.freeRegistrationDialog === dialog && this.freeRegistrationEvent === event &&
        this.freeRegistrationGeneration === pending.generation;
      try {
        const response = await fetch(`${eventforgeApiBase}/functions/v1/register-free`, {
          method: 'POST',
          credentials: 'omit',
          redirect: 'error',
          signal: AbortSignal.timeout(20000),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            apikey: eventforgePublicKey,
            Authorization: `Bearer ${eventforgePublicKey}`
          },
          body: JSON.stringify({ event_id: event.eventforgeId, attendee_name: `${firstName} ${lastName}`, attendee_email: email, locale: 'de' })
        });
        const payload = await response.json();
        if (!current()) return;
        if (!response.ok || response.redirected || payload?.error || typeof payload?.ticket_code !== 'string' || !payload.ticket_code.trim()) {
          if (payload?.error === 'Registration has closed') {
            error.textContent = 'Die Online-Anmeldung ist bereits geschlossen. Du kannst weiterhin vorbeikommen.';
            error.hidden = false;
            return;
          }
          throw new Error('Unconfirmed registration');
        }
        dialog.querySelector('[data-rsvp-entry]').hidden = true;
        dialog.querySelector('[data-rsvp-success]').hidden = false;
        dialog.querySelector('[data-rsvp-success-text]').textContent = `Deine kostenlose Anmeldung für „${event.title}“ ist bestätigt. Ticketcode: ${payload.ticket_code.trim()}`;
        dialog.querySelector('[data-rsvp-done]')?.focus();
      } catch (_requestError) {
        if (current()) {
          error.textContent = 'Die Anmeldung konnte nicht bestätigt werden. Bitte prüfe deine E-Mails, bevor du es erneut versuchst. Du kannst auch ohne Anmeldung vorbeikommen.';
          error.hidden = false;
        }
      } finally {
        if (this.freeRegistrationPending === pending) this.freeRegistrationPending = null;
        form.removeAttribute('aria-busy');
        const activeSubmit = this.freeRegistrationDialog?.querySelector('[data-rsvp-submit]');
        if (activeSubmit) {
          activeSubmit.disabled = false;
          activeSubmit.textContent = 'Kostenlos anmelden';
        }
      }
    }

    ensureWaitlistDialog() {
      if (this.waitlistDialog?.isConnected) return this.waitlistDialog;

      const dialog = document.createElement('dialog');
      const titleId = `EventforgeWaitlistTitle-${Math.random().toString(36).slice(2, 10)}`;
      dialog.className = 'eventforge-waitlist-dialog';
      dialog.setAttribute('aria-labelledby', titleId);
      dialog.innerHTML = `
        <div class="eventforge-waitlist-dialog__panel">
          <button class="eventforge-waitlist-dialog__close" type="button" data-waitlist-close aria-label="Warteliste schließen">×</button>
          <div data-waitlist-entry>
            <p class="eventforge-waitlist-dialog__eyebrow">Warteliste</p>
            <h2 class="eventforge-waitlist-dialog__title" id="${titleId}" data-waitlist-title></h2>
            <p class="eventforge-waitlist-dialog__meta" data-waitlist-meta></p>
            <p class="eventforge-waitlist-dialog__intro">Sobald ein Platz frei wird, benachrichtigt dich EventForge per E-Mail.</p>
            <form class="eventforge-waitlist-form" data-waitlist-form>
              <div class="eventforge-waitlist-form__row">
                <label class="eventforge-waitlist-form__field">
                  <span>Vorname</span>
                  <input name="first_name" type="text" autocomplete="given-name" maxlength="50" required>
                </label>
                <label class="eventforge-waitlist-form__field">
                  <span>Nachname</span>
                  <input name="last_name" type="text" autocomplete="family-name" maxlength="50" required>
                </label>
              </div>
              <label class="eventforge-waitlist-form__field">
                <span>E-Mail-Adresse</span>
                <input name="email" type="email" autocomplete="email" maxlength="255" required>
              </label>
              <p class="eventforge-waitlist-form__privacy">Deine Angaben werden nur für diese Warteliste und das Platzangebot verwendet.</p>
              <p class="eventforge-waitlist-form__error" data-waitlist-error role="alert" hidden></p>
              <button class="eventforge-waitlist-form__submit" type="submit" data-waitlist-submit>Auf die Warteliste</button>
            </form>
          </div>
          <div class="eventforge-waitlist-success" data-waitlist-success role="status" hidden>
            <span class="eventforge-waitlist-success__icon" aria-hidden="true">✓</span>
            <h2>Du bist auf der Warteliste!</h2>
            <p data-waitlist-success-text></p>
            <button class="eventforge-waitlist-form__submit" type="button" data-waitlist-done>Fertig</button>
          </div>
        </div>
      `;

      const close = () => this.closeWaitlist();
      dialog.querySelector('[data-waitlist-close]')?.addEventListener('click', close);
      dialog.querySelector('[data-waitlist-done]')?.addEventListener('click', close);
      dialog.querySelector('[data-waitlist-form]')?.addEventListener('submit', (event) => this.submitWaitlist(event));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('eventforge-waitlist-open');
        this.waitlistEvent = null;
      });

      document.body.append(dialog);
      this.waitlistDialog = dialog;
      return dialog;
    }

    openWaitlist(event) {
      if (this.eventState(event) !== 'upcoming') return;
      if (!this.canJoinWaitlist(event)) {
        const destination = this.destinationUrl(event);
        if (destination) window.location.assign(destination);
        return;
      }

      this.waitlistEvent = event;
      const dialog = this.ensureWaitlistDialog();
      const form = dialog.querySelector('[data-waitlist-form]');
      const entry = dialog.querySelector('[data-waitlist-entry]');
      const success = dialog.querySelector('[data-waitlist-success]');
      const error = dialog.querySelector('[data-waitlist-error]');
      form?.reset();
      if (entry) entry.hidden = false;
      if (success) success.hidden = true;
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      const title = dialog.querySelector('[data-waitlist-title]');
      const meta = dialog.querySelector('[data-waitlist-meta]');
      if (title) title.textContent = event.title;
      if (meta) {
        meta.textContent = [this.formatDateTime(event.date), this.shortLocation(event.location)]
          .filter(Boolean)
          .join(' · ');
      }

      if (form) {
        form.elements.first_name.value = this.customer.firstName;
        form.elements.last_name.value = this.customer.lastName;
        form.elements.email.value = this.customer.email;
      }

      document.documentElement.classList.add('eventforge-waitlist-open');
      if (!dialog.open && typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else if (!dialog.open) {
        dialog.setAttribute('open', '');
      }
      requestAnimationFrame(() => {
        const firstEmptyField = Array.from(form?.elements || []).find((field) => field.tagName === 'INPUT' && !field.value);
        firstEmptyField?.focus();
      });
    }

    closeWaitlist() {
      if (!this.waitlistDialog) return;
      if (this.waitlistDialog.open && typeof this.waitlistDialog.close === 'function') {
        this.waitlistDialog.close();
      } else {
        this.waitlistDialog.removeAttribute('open');
        document.documentElement.classList.remove('eventforge-waitlist-open');
      }
      this.waitlistEvent = null;
    }

    async submitWaitlist(submitEvent) {
      submitEvent.preventDefault();
      const event = this.waitlistEvent;
      const dialog = this.waitlistDialog;
      const form = submitEvent.currentTarget;
      if (!event || !dialog || !this.canJoinWaitlist(event) || !form.reportValidity()) return;

      const submitButton = dialog.querySelector('[data-waitlist-submit]');
      const error = dialog.querySelector('[data-waitlist-error]');
      const firstName = form.elements.first_name.value.trim();
      const lastName = form.elements.last_name.value.trim();
      const email = form.elements.email.value.trim().toLocaleLowerCase('de-DE');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Wird hinzugefügt …';
      }

      try {
        const response = await fetch(`${eventforgeApiBase}/rest/v1/waitlist`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
            apikey: eventforgePublicKey,
            Authorization: `Bearer ${eventforgePublicKey}`
          },
          body: JSON.stringify([{
            event_id: event.eventforgeId,
            organization_id: event.organizationId,
            attendee_name: `${firstName} ${lastName}`.trim(),
            attendee_email: email
          }])
        });

        if (!response.ok) {
          let payload = {};
          try {
            payload = await response.json();
          } catch (_error) {
            // The status code is enough to provide a safe fallback message.
          }
          const code = String(payload.code || '');
          const message = String(payload.message || '');
          if (code === '23505' || code === '23514' || /duplicate key|unique constraint/i.test(message)) {
            throw new Error('Diese E-Mail-Adresse steht bereits auf der Warteliste.');
          }
          if (code === '42501' || /row-level security|violates row-level/i.test(message)) {
            throw new Error('Die Warteliste ist für dieses Event nicht mehr geöffnet.');
          }
          throw new Error('Die Anmeldung konnte gerade nicht gespeichert werden. Bitte versuche es erneut.');
        }

        const entry = dialog.querySelector('[data-waitlist-entry]');
        const success = dialog.querySelector('[data-waitlist-success]');
        const successText = dialog.querySelector('[data-waitlist-success-text]');
        if (entry) entry.hidden = true;
        if (success) success.hidden = false;
        if (successText) successText.textContent = `Wir benachrichtigen dich unter ${email}, sobald ein Platz frei wird.`;
        dialog.querySelector('[data-waitlist-done]')?.focus();
      } catch (requestError) {
        if (error) {
          error.textContent = requestError?.message || 'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut.';
          error.hidden = false;
        }
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = 'Auf die Warteliste';
        }
      }
    }

    formatDateTime(date) {
      return new Intl.DateTimeFormat('de-DE', {
        timeZone: this.timeZone,
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date).replace(',', ' ·');
    }

    formatEventTime(event) {
      const start = this.formatTime(event.date);
      return event.endDate ? `${start}–${this.formatTime(event.endDate)}` : start;
    }

    formatEventDateTime(event, compact = false) {
      const start = compact ? this.formatCompactDateTime(event.date) : this.formatDateTime(event.date);
      if (!event.endDate) return start;
      const end = this.dateKey(event.date) === this.dateKey(event.endDate)
        ? this.formatTime(event.endDate)
        : this.formatDateTime(event.endDate);
      return `${start}–${end}`;
    }

    formatTime(date) {
      return new Intl.DateTimeFormat('de-DE', {
        timeZone: this.timeZone,
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }

    element(tagName, className, text) {
      const element = document.createElement(tagName);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }

    setLoading(isLoading) {
      this.setAttribute('aria-busy', String(isLoading));
      if (this.status) this.status.hidden = !isLoading;
      if (this.content) this.content.hidden = isLoading;
    }

    showLoading() {
      if (this.status) {
        const inner = this.element('div', 'eventforge-calendar__status-inner');
        const spinner = this.element('span', 'eventforge-calendar__spinner');
        spinner.setAttribute('aria-hidden', 'true');
        inner.append(spinner, this.element('span', '', 'Termine werden geladen …'));
        this.status.replaceChildren(inner);
      }
      this.setLoading(true);
    }

    showError(message) {
      this.setAttribute('aria-busy', 'false');
      if (this.content) this.content.hidden = true;
      if (!this.status) return;
      this.status.hidden = false;
      const inner = this.element('div', 'eventforge-calendar__status-inner');
      inner.append(this.element('p', '', message));
      const retry = this.element('button', 'eventforge-calendar__button', 'Erneut versuchen');
      retry.type = 'button';
      retry.addEventListener('click', () => this.loadEvents(), { once: true });
      inner.append(retry);
      const external = this.element('a', '', 'Events direkt bei EventForge öffnen');
      external.href = this.storeUrl;
      inner.append(external);
      this.status.replaceChildren(inner);
    }
  }

  customElements.define('eventforge-calendar', EventforgeCalendar);
})();
